/**
 * KYB queue (moderator) — every company with its verification state, verdict,
 * badge and current evidence (`GET /verification/moderator/companies/`).
 * A second tab keeps the raw check log (`/checks/`).
 *
 * Nothing here runs a paid check: the moderator opens a company and chooses
 * a provider there (product rule 1 in docs/decisions/kyb.md).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { unwrapPaginated } from "@/config/api";
import { formatDate } from "@/i18n/format";
import {
  CHECK_DECISIONS,
  CHECK_STATUS_CHIP,
  CHECK_STATUSES,
  type CheckDecisionKey,
  type CheckStatusKey,
  checkDecisionKey,
  checkStatusKey,
  DECISION_CHIP,
  jurisdictionLabel,
  QUEUE_STATES,
  STATE_CHIP,
  VERDICT_CHIP,
} from "@/lib/kyb";
import type {
  VerificationCheck,
  VerificationModeratorChecksListParams,
  VerificationModeratorCompaniesListParams,
  VerificationQueueRow,
} from "@/services/moderators/generated/model";
import {
  useVerificationModeratorChecksList,
  useVerificationModeratorCompaniesList,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";
import { KybHowItWorks } from "./KybFlowSteps";
import { useBadgeLabels, useFactLabels } from "./kybLabels";
import "../ModeratorPage.css";
import "./Kyb.css";

type Tab = "all" | (typeof QUEUE_STATES)[number] | "checks";

const ModeratorKybQueuePage = () => (
  <ModeratorLayout>
    <Content />
  </ModeratorLayout>
);

const Content = () => {
  const { t } = useTranslation("kyb");
  const [tab, setTab] = useState<Tab>("all");
  const tabs: Tab[] = ["all", ...QUEUE_STATES, "checks"];

  return (
    <div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("queue.title")}</h1>
          <p className="mod-page__subtitle">{t("queue.subtitle")}</p>
        </div>
        <Link to="/moderator/kyb/badges" className="mod-page__btn mod-page__btn--ghost">
          <i className="fa-solid fa-award" /> {t("queue.badgeTypes")}
        </Link>
      </header>

      <KybHowItWorks />

      <div className="kyb-tabs" role="tablist">
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`kyb-tabs__tab ${tab === key ? "kyb-tabs__tab--active" : ""}`}
            onClick={() => setTab(key)}
          >
            {t(`queue.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === "checks" ? <CheckLog /> : <CompanyQueue state={tab === "all" ? undefined : tab} />}
    </div>
  );
};

// ------------------------------------------------------------------ Companies

const CompanyQueue = ({ state }: { state?: (typeof QUEUE_STATES)[number] }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const params: VerificationModeratorCompaniesListParams = { page, page_size: 25 };
  if (state) params.state = state;
  if (search.trim()) params.search = search.trim();
  const { data, isLoading, error } = useVerificationModeratorCompaniesList(params);
  const rows = unwrapPaginated<VerificationQueueRow>(data);
  const factLabel = useFactLabels();
  const badgeLabel = useBadgeLabels();
  const hasNext = Boolean((data as { next?: string | null } | undefined)?.next);

  return (
    <>
      <div className="mod-page__toolbar">
        <input
          className="mod-page__input"
          placeholder={t("queue.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>
      {state ? <p className="kyb-section__help">{t(`queue.stateHelp.${state}`)}</p> : null}

      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("queue.loadError")}</div> : null}
      {!isLoading && !error && rows.length === 0 ? <div className="mod-page__empty">{t("queue.empty")}</div> : null}

      {rows.length > 0 ? (
        <div className="kyb-section kyb-scroll">
          <table className="kyb-table kyb-table--clickable">
            <thead>
              <tr>
                <th>{t("queue.columns.company")}</th>
                <th>{t("queue.columns.registrations")}</th>
                <th>{t("queue.columns.state")}</th>
                <th>{t("queue.columns.evidence")}</th>
                <th>{t("queue.columns.badge")}</th>
                <th>{t("queue.columns.next")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.companyId} onClick={() => navigate(`/moderator/kyb/companies/${row.companyId}`)}>
                  <td>
                    <Link to={`/moderator/kyb/companies/${row.companyId}`} onClick={(e) => e.stopPropagation()}>
                      <strong>{row.companyName}</strong>
                    </Link>
                    <div className="kyb-note">
                      {row.country}
                      {row.isApproved ? "" : ` · ${t("queue.notApproved")}`}
                    </div>
                  </td>
                  <td>
                    {row.registrations.length === 0 ? (
                      <span className="mod-page__chip mod-page__chip--danger">{t("queue.noRegistration")}</span>
                    ) : (
                      row.registrations.map((r) => (
                        <div key={r.id}>
                          <span className="mod-page__chip">{jurisdictionLabel(r.country, r.subdivision)}</span>
                          {r.registrationNumber}
                        </div>
                      ))
                    )}
                  </td>
                  <td>
                    <span className={`mod-page__chip ${STATE_CHIP[row.state]}`}>{t(`queue.state.${row.state}`)}</span>
                    <div>
                      <span className={`mod-page__chip ${VERDICT_CHIP[row.verdict]}`}>
                        {t("verdict.prefix", { verdict: t(`verdict.${row.verdict}`) })}
                      </span>
                    </div>
                  </td>
                  <td>
                    {row.evidence.length === 0 ? (
                      <span className="kyb-note">{t("queue.noEvidence")}</span>
                    ) : (
                      row.evidence.map((fact) => (
                        <span key={fact} className="mod-page__chip mod-page__chip--success">
                          {factLabel(fact)}
                        </span>
                      ))
                    )}
                  </td>
                  <td>
                    {row.badge ? (
                      <span className="mod-page__chip mod-page__chip--success">{badgeLabel(row.badge)}</span>
                    ) : (
                      <span className="kyb-note">{t("queue.noBadge")}</span>
                    )}
                    {row.suggestedBadge && row.suggestedBadge !== row.badge ? (
                      <div className="kyb-note">{t("queue.suggested", { badge: badgeLabel(row.suggestedBadge) })}</div>
                    ) : null}
                  </td>
                  <td>
                    <NextAction row={row} badgeLabel={badgeLabel} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="mod-page__toolbar">
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          {t("common:actions.previous")}
        </button>
        <button
          type="button"
          className="mod-page__btn mod-page__btn--ghost"
          disabled={!hasNext}
          onClick={() => setPage((p) => p + 1)}
        >
          {t("common:actions.next")}
        </button>
      </div>
    </>
  );
};

/** One line saying what the moderator would do next for this company. */
const NextAction = ({ row, badgeLabel }: { row: VerificationQueueRow; badgeLabel: (key: string) => string }) => {
  const { t } = useTranslation("kyb");
  if (row.registrations.length === 0) return <span className="kyb-note">{t("queue.nextAction.askRegistration")}</span>;
  if (row.openChecks.length > 0) {
    return (
      <>
        {row.openChecks.map((c) => (
          <div key={c.id}>
            <span className={`mod-page__chip ${CHECK_STATUS_CHIP[checkStatusKey(c.status)]}`}>
              {c.provider} · {t(`check.status.${checkStatusKey(c.status)}`)}
            </span>
          </div>
        ))}
      </>
    );
  }
  if (row.state === "awaiting_badge" && row.suggestedBadge) {
    return <span>{t("queue.nextAction.award", { badge: badgeLabel(row.suggestedBadge) })}</span>;
  }
  if (row.state === "verified") return <span className="kyb-note">{t("queue.nextAction.none")}</span>;
  if (row.recommendedProvider) {
    return <span>{t("queue.nextAction.run", { provider: row.recommendedProvider })}</span>;
  }
  return <span>{t("queue.nextAction.manual")}</span>;
};

// ------------------------------------------------------------------ Check log

const CheckLog = () => {
  const { t } = useTranslation(["kyb", "common"]);
  const [decision, setDecision] = useState<"all" | CheckDecisionKey>("all");
  const [status, setStatus] = useState<"all" | CheckStatusKey>("all");

  const params: VerificationModeratorChecksListParams = {};
  if (decision !== "all") params.decision = decision;
  if (status !== "all") params.status = status;
  const { data, isLoading, error } = useVerificationModeratorChecksList(params);
  const items = unwrapPaginated<VerificationCheck>(data);

  return (
    <>
      <div className="mod-page__toolbar">
        <select
          className="mod-page__select"
          value={decision}
          onChange={(e) => setDecision(e.target.value as typeof decision)}
          aria-label={t("queue.log.decisionLabel")}
        >
          <option value="all">{t("queue.log.allDecisions")}</option>
          {CHECK_DECISIONS.map((k) => (
            <option key={k} value={k}>
              {t(`check.decision.${k}`)}
            </option>
          ))}
        </select>
        <select
          className="mod-page__select"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          aria-label={t("queue.log.statusLabel")}
        >
          <option value="all">{t("queue.log.allStatuses")}</option>
          {CHECK_STATUSES.map((k) => (
            <option key={k} value={k}>
              {t(`check.status.${k}`)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("queue.loadError")}</div> : null}
      {!isLoading && items.length === 0 ? <div className="mod-page__empty">{t("queue.log.empty")}</div> : null}

      {items.length > 0 ? (
        <div className="kyb-section kyb-scroll">
          <table className="kyb-table">
            <thead>
              <tr>
                <th>{t("queue.log.columns.check")}</th>
                <th>{t("queue.columns.company")}</th>
                <th>{t("queue.log.columns.provider")}</th>
                <th>{t("queue.log.columns.covered")}</th>
                <th>{t("queue.columns.state")}</th>
                <th>{t("queue.log.columns.created")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((check) => (
                <tr key={check.id}>
                  <td>#{check.id}</td>
                  <td>
                    <Link to={`/moderator/kyb/companies/${check.companyId}`}>{check.companyName}</Link>
                  </td>
                  <td>{check.provider}</td>
                  <td>
                    {check.covered.map((c) => (
                      <span key={c} className="mod-page__chip">
                        {c}
                      </span>
                    ))}
                  </td>
                  <td>
                    <span className={`mod-page__chip ${CHECK_STATUS_CHIP[checkStatusKey(check.status)]}`}>
                      {t(`check.status.${checkStatusKey(check.status)}`)}
                    </span>
                    <span className={`mod-page__chip ${DECISION_CHIP[checkDecisionKey(check.decision)]}`}>
                      {t(`check.decision.${checkDecisionKey(check.decision)}`)}
                    </span>
                  </td>
                  <td>{formatDate(check.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
};

export default ModeratorKybQueuePage;
